import { CONSENT_COOKIE_MAX_AGE, CONSENT_COOKIE_MAX_LENGTH, CONSENT_COOKIE_NAME } from './constants';
import { IConsentCookieValue } from './types';

function readCookie(name: string): string {
    if (typeof document === 'undefined') return '';
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[1]) : '';
}

export function readConsentCookie(): IConsentCookieValue | null {
    const raw = readCookie(CONSENT_COOKIE_NAME);
    if (!raw || raw.length > CONSENT_COOKIE_MAX_LENGTH) return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.analyticsConsentGranted === 'boolean') {
            return parsed as IConsentCookieValue;
        }
    } catch {
        // malformed cookie - treat as undecided
    }
    return null;
}

// Derive the registrable parent domain (last two labels of the hostname) so the
// consent cookie is shared across sibling subdomains (e.g. the Keycloak login host
// and the app host both see one decision). Assumes simple two-label suffixes like
// `example.com`; does not handle multi-part suffixes such as `co.uk`.
export function getSharedCookieDomain(hostname: string): string {
    return hostname.split('.').slice(-2).join('.');
}

export function writeConsentCookie(
    value: IConsentCookieValue,
    domain: string = getSharedCookieDomain(window.location.hostname),
): void {
    if (typeof document === 'undefined') return;
    const json = JSON.stringify(value);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
        `${CONSENT_COOKIE_NAME}=${encodeURIComponent(json)}` +
        `; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax${secure}; Domain=${domain}`;
}

// KEEP IN SYNC with the non-shared JSP duplicate in
// ghix-web/src/main/webapp/resources/js/consent/consentCookie.js (`buildConsentValue`).
// The cookie shape (key names + order) must match across surfaces; shipping different
// shapes would split a single user's decision into two and break the shared cookie.
export function buildConsentValue(granted: boolean): IConsentCookieValue {
    return { analyticsConsentGranted: granted };
}
